import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getUserById, updateUser } from "@/services/userService";
import { UserRole } from "@/types/user";
import { toast } from "sonner";
import { queryKeys } from "@/lib/queryKeys";

const schema = z.object({
  displayName: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().email("Email inválido").optional().or(z.literal("")),
  phone: z.string().optional(),
  role: z.nativeEnum(UserRole),
});

type FormData = z.infer<typeof schema>;

const roleOptions = [
  { value: UserRole.ADMIN, label: "Admin" },
  { value: UserRole.CLIENT, label: "Cliente" },
  { value: UserRole.SELLER, label: "Vendedor" },
  { value: UserRole.MECHANIC, label: "Mecánico" },
  { value: UserRole.GUARANTEER, label: "Garantizador" },
  { value: UserRole.SUPPLIER, label: "Suplidor" },
];

export function UserForm() {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(isEdit);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { role: UserRole.CLIENT },
  });

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    getUserById(id).then((u) => {
      if (u) {
        setValue("displayName", u.displayName ?? "");
        setValue("firstName", u.firstName ?? "");
        setValue("lastName", u.lastName ?? "");
        setValue("email", u.email ?? "");
        setValue("phone", u.phone ?? "");
        setValue("role", (u.role as UserRole) ?? UserRole.CLIENT);
      }
      setLoading(false);
    });
  }, [id, setValue]);

  const onSubmit = async (data: FormData) => {
    if (!id) return;
    try {
      await updateUser(id, {
        displayName: data.displayName,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        role: data.role,
      });
      toast.success("Usuario actualizado");
      await queryClient.invalidateQueries({ queryKey: queryKeys.users });
      navigate("/users");
    } catch {
      toast.error("Error al guardar");
    }
  };

  if (isEdit && loading) return <p className="text-muted-foreground">Cargando…</p>;

  if (!isEdit) {
    return (
      <div className="max-w-2xl space-y-6">
        <p className="text-muted-foreground">
          Crear usuario desde aquí requiere integración con Firebase Auth (crear usuario con email/contraseña y luego
          documento en Firestore). Por ahora usa la app móvil para crear usuarios o edita los existentes.
        </p>
        <Button variant="outline" onClick={() => navigate("/users")}>
          Volver a usuarios
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="font-display text-2xl font-semibold">Editar usuario</h1>
      <Card>
        <CardHeader>
          <CardTitle>Datos del usuario</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre para mostrar</Label>
              <Input {...register("displayName")} placeholder="Ej. Juan Pérez" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input {...register("firstName")} />
              </div>
              <div className="space-y-2">
                <Label>Apellido</Label>
                <Input {...register("lastName")} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input {...register("email")} type="email" disabled className="bg-muted" />
              <p className="text-xs text-muted-foreground">El email no se puede cambiar aquí.</p>
            </div>
            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input {...register("phone")} placeholder="Opcional" />
            </div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select value={watch("role")} onValueChange={(v) => setValue("role", v as UserRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Guardando…" : "Guardar"}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate("/users")}>
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
